import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type { User, Client, Quote, Policy, Risk, Asset, Invoice, Payment, Schedule, FAP, Adviser, AdviserPermissionRole, SystemUser, Renewal, Transaction } from '../types';
import { getClientName } from '../types';
import { mockAssets, mockPayments, mockSchedules, mockTransactions } from '../data/mockData';
import { setAuth0TokenGetter } from '../lib/api-client';
import { initializeApiClient, setApiAuth0TokenGetter, FaPsService, ClientsService, UsersService, InvoicesService, RenewalsService } from '../api/config';
import type { FapResponseDto, AdviserResponseDto } from '../api/config';
import { setUnauthorizedHandler } from '../lib/auth-redirect';
import { getAuth0LogoutReturnTo } from '../lib/auth0-env';
import { transformClientToFrontend } from '../lib/transformers/client';
import { transformInvoiceToFrontend } from '../lib/transformers/invoice';
import { transformRenewalToFrontend } from '../components/renewals/transformers';
import { clearDatadogRumUser, setDatadogRumUser } from '../lib/datadog-rum';
import { canViewInvoices } from '../lib/permissions';

type Page = 'login' | 'dashboard' | 'business-admin-dashboard' | 'finance-admin-dashboard' | 'clients' | 'client-profile' | 'quotes' | 'quote-create' | 'renewals' | 'renewal-detail' | 'renewal-amendment' | 'reports' | 'create-person' | 'create-organisation' | 'house-quote' | 'contents-quote' | 'vehicle-quote' | 'asset-detail' | 'policies' | 'invoices' | 'combined-quote-builder' | 'users' | 'fap-detail' | 'adviser-detail' | 'user-detail' | 'debtors-report' | 'policy-detail' | 'eoffer-demo' | 'invoice-detail' | 'mta-workflow' | 'cash-credits-report' | 'adviser-performance-report' | 'bordeaux-report' | 'notifications';

// Initialize API client on module load
initializeApiClient();

type Auth0Profile = {
  sub?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  [key: string]: unknown;
};

const getStringValue = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return undefined;

  if (typeof value === 'object') {
    if ('value' in value && typeof value.value === 'string') return value.value;
    if ('text' in value && typeof value.text === 'string') return value.text;
  }

  return undefined;
};

const getDataArray = <T,>(response: unknown): T[] => {
  if (Array.isArray(response)) {
    return response as T[];
  }

  if (
    response &&
    typeof response === 'object' &&
    'data' in response &&
    Array.isArray(response.data)
  ) {
    return response.data as T[];
  }

  return [];
};

/**
 * Transform API FAP response to frontend FAP type
 */
function transformFapToFrontend(apiData: FapResponseDto): FAP {
  return {
    id: apiData.id,
    legalName: apiData.legalName || '',
    tradingName: getStringValue(apiData.tradingName),
    fapLicenceNumber: apiData.fapLicenceNumber || '',
    fsprNumber: apiData.fsprNumber || '',
    gstNumber: getStringValue(apiData.gstNumber),
    commissionPercentage: apiData.commissionPercentage ?? undefined,
    brokerFeePercentage: apiData.brokerFeePercentage ?? undefined,
    qaCommissionPercentage: apiData.qaCommissionPercentage ?? undefined,
    qaRenewalCommissionPercentage: apiData.qaRenewalCommissionPercentage ?? undefined,
    primaryContact: {
      name: apiData.primaryContactName || '',
      email: apiData.primaryContactEmail || '',
      phone: apiData.primaryContactPhone || '',
    },
    registeredAddress: {
      street: '',
      suburb: '',
      city: '',
      postcode: '',
      country: 'New Zealand',
    },
    banking: {
      accountName: apiData.bankingAccountName || '',
      accountNumber: apiData.bankingAccountNumber || '',
      referenceFormat: getStringValue(apiData.bankingReferenceFormat),
    },
    documents: [],
    status: apiData.status as 'active' | 'inactive',
    notes: getStringValue(apiData.notes),
    createdAt: apiData.createdAt,
    updatedAt: apiData.updatedAt,
  };
}

/**
 * Transform API Adviser response to frontend Adviser type
 */
function transformAdviserToFrontend(apiData: AdviserResponseDto): Adviser {
  // Get firstName and lastName from API data
  const firstName = getStringValue(apiData.firstName) || '';
  const lastName = getStringValue(apiData.lastName) || '';

  // Get FAP name - try tradingName first, then legalName
  const fapTradingName = apiData.fap?.tradingName ? getStringValue(apiData.fap.tradingName) : undefined;
  const fapLegalName = apiData.fap?.legalName || '';
  const fapName = fapTradingName || fapLegalName || 'Unknown FAP';
  const roles = apiData.roles.map((role): AdviserPermissionRole => {
    if (role === 'ADVISER') return 'adviser';
    if (role === 'BUSINESS_ADMIN' || role === 'FINANCE_ADMIN' || role === 'SUPER_ADMIN') {
      return 'admin';
    }
    return 'adviser';
  });
  const toOptionalNumber = (value: number | null | undefined): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    return undefined;
  };

  return {
    id: apiData.id,
    adviserId: getStringValue(apiData.adviserId),
    fapId: getStringValue(apiData.fapId) || '',
    fapName,
    firstName,
    lastName,
    email: apiData.emailAddress,
    phone: undefined, // Not in API response
    mobile: undefined, // Not in API response
    adviserRole: apiData.adviserRole ?? null,
    fsprNumber: getStringValue(apiData.fsprNumber),
    roles,
    canRefer: true,
    canQuote: true,
    canBind: true,
    isManager: apiData.roles.includes('BUSINESS_ADMIN') || apiData.roles.includes('SUPER_ADMIN'),
    commissionRecipient: 'adviser' as const,
    hasCommissionOverride: false,
    commissionPercentage: toOptionalNumber(apiData.commissionPercentage),
    overridePercentage: toOptionalNumber(apiData.overridePercentage),
    overrideName: getStringValue(apiData.overrideName),
    brokerFeePercentage: toOptionalNumber(apiData.brokerFeePercentage),
    qaCommissionPercentage: toOptionalNumber(apiData.qaCommissionPercentage),
    qaRenewalCommissionPercentage: toOptionalNumber(apiData.qaRenewalCommissionPercentage),
    status: apiData.status as 'active' | 'inactive',
    documents: [],
    createdAt: apiData.createdAt,
    updatedAt: apiData.updatedAt,
    lastLogin: getStringValue(apiData.lastLogin),
  };
}

interface NavigationHistoryItem {
  page: Page;
  label: string;
  clientId?: string;
  quoteId?: string;
  assetId?: string;
}

interface AppContextType {
  // Auth
  currentUser: User | null;
  login: (email: string, password: string) => boolean;
  logout: () => void;

  // Navigation
  currentPage: Page;
  setCurrentPage: (page: Page) => void;
  navigateWithHistory: (page: Page, label: string, context?: { clientId?: string; quoteId?: string; assetId?: string }) => void;
  navigateBack: () => void;
  navigationHistory: NavigationHistoryItem[];
  getBreadcrumbs: () => NavigationHistoryItem[];

  // Data
  clients: Client[];
  quotes: Quote[];
  policies: Policy[];
  risks: Risk[];
  assets: Asset[];
  invoices: Invoice[];
  payments: Payment[];
  schedules: Schedule[]
  faps: FAP[];
  advisers: Adviser[];
  renewals: Renewal[];
  transactions: Transaction[];

  // Loading states
  isLoadingFaps: boolean;
  isLoadingClients: boolean;
  isLoadingAdvisers: boolean;

  // API error states
  fapsError: string | null;
  clientsError: string | null;
  advisersError: string | null;

  // Refresh functions
  refreshFaps: () => Promise<void>;
  refreshClients: () => Promise<void>;
  refreshAdvisers: (fapId?: string, search?: string) => Promise<void>;
  refreshInvoices: () => Promise<void>;
  refreshRenewals: () => Promise<void>;

  // Selected items
  selectedClient: Client | null;
  setSelectedClient: (client: Client | null) => void;
  selectedQuote: Quote | null;
  setSelectedQuote: (quote: Quote | null) => void;
  selectedRisk: Risk | null;
  setSelectedRisk: (risk: Risk | null) => void;
  selectedAsset: Asset | null;
  setSelectedAsset: (asset: Asset | null) => void;
  selectedPolicy: Policy | null;
  setSelectedPolicy: (policy: Policy | null) => void;
  selectedFAP: FAP | null;
  setSelectedFAP: (fap: FAP | null) => void;
  selectedAdviser: Adviser | null;
  setSelectedAdviser: (adviser: Adviser | null) => void;
  selectedSystemUser: SystemUser | null;
  setSelectedSystemUser: (user: SystemUser | null) => void;
  selectedInvoice: Invoice | null;
  setSelectedInvoice: (invoice: Invoice | null) => void;
  selectedRenewal: Renewal | null;
  setSelectedRenewal: (renewal: Renewal | null) => void;

  // CRUD operations
  addClient: (client: Client) => void;
  updateClient: (client: Client) => void;
  addQuote: (quote: Quote) => void;
  updateQuote: (quoteId: string, updates: Partial<Quote>) => void;
  addRisk: (risk: Risk) => void;
  updateRisk: (risk: Risk) => void;
  addAsset: (asset: Asset) => void;
  updateAsset: (asset: Asset) => void;
  updatePolicy: (policyId: string, updates: Partial<Policy>) => void;
  updateTransaction: (transactionId: string, updates: Partial<Transaction>) => void;
  addTransactions: (transactions: Transaction[]) => void;

  // Filters
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user: auth0User, isAuthenticated, isLoading: auth0Loading, logout: auth0Logout, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState<Page>('login');
  const [navigationHistory, setNavigationHistory] = useState<NavigationHistoryItem[]>([]);
  // Initialize with mock data as fallback, will be replaced by API data when available
  const [clients, setClients] = useState<Client[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [risks, setRisks] = useState<Risk[]>([]);
  const [assets, setAssets] = useState<Asset[]>(mockAssets);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, _setPayments] = useState<Payment[]>(mockPayments);
  const [schedules, _setSchedules] = useState<Schedule[]>(mockSchedules);
  const [faps, setFaps] = useState<FAP[]>([]);
  const [advisers, setAdvisers] = useState<Adviser[]>([]);
  const [renewals, setRenewals] = useState<Renewal[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>(mockTransactions);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [selectedRisk, setSelectedRisk] = useState<Risk | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [selectedFAP, setSelectedFAP] = useState<FAP | null>(null);
  const [selectedAdviser, setSelectedAdviser] = useState<Adviser | null>(null);
  const [selectedSystemUser, setSelectedSystemUser] = useState<SystemUser | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedRenewal, setSelectedRenewal] = useState<Renewal | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Loading and error states for API calls
  const [isLoadingFaps, setIsLoadingFaps] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingAdvisers, setIsLoadingAdvisers] = useState(false);
  const [fapsError, setFapsError] = useState<string | null>(null);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [advisersError, setAdvisersError] = useState<string | null>(null);

  // Use ref to track current Auth0 loading state for token getter
  const auth0LoadingRef = useRef(auth0Loading);
  useEffect(() => {
    auth0LoadingRef.current = auth0Loading;
  }, [auth0Loading]);

  // Set up Auth0 token getter for both API clients
  useEffect(() => {
    const tokenGetter = async () => {
      // Wait for Auth0 to finish initializing before attempting to get token
      // This is critical for direct page loads/refreshes where Auth0 is still checking the session
      // We use a ref to check the current loading state since this function is called asynchronously
      if (auth0LoadingRef.current) {
        // Wait for Auth0 to finish loading (max 2 seconds)
        // This handles the race condition where API calls are made before Auth0 finishes initializing
        let attempts = 0;
        const maxAttempts = 20; // 2 seconds total (20 * 100ms)
        while (auth0LoadingRef.current && attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        // If still loading after waiting, try to get token anyway
        // getAccessTokenSilently should handle waiting internally
      }

      try {
        // Always try to get token silently - getAccessTokenSilently will:
        // 1. Wait for Auth0 to finish initializing if needed (handles race conditions)
        // 2. Use cached tokens if available
        // 3. Refresh tokens if needed
        // 4. Throw only if user is truly not authenticated
        // This works even if isAuthenticated appears false but Auth0 is still checking the session
        const token = await getAccessTokenSilently();
        return token || null;
      } catch (error) {
        // getAccessTokenSilently throws if:
        // - User is not authenticated
        // - There's an error getting the token
        // - Auth0 is still initializing (in some edge cases)
        // Return null instead of throwing to allow components to handle it gracefully
        // The 401 handler will redirect to login if needed
        return null;
      }
    };

    // Set token getter for both API clients
    setAuth0TokenGetter(tokenGetter);
    setApiAuth0TokenGetter(tokenGetter);
  }, [isAuthenticated, getAccessTokenSilently]);

  // Set up unauthorized handler for 401 redirects
  useEffect(() => {
    const unauthorizedHandler = () => {
      // Store the current path to return to after login
      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      const returnTo = currentPath !== '/' ? currentPath : undefined;

      // Redirect to Auth0 login page
      loginWithRedirect({
        appState: {
          returnTo: returnTo || '/dashboard',
        },
      });
    };

    setUnauthorizedHandler(unauthorizedHandler);
  }, [loginWithRedirect]);

  /**
   * Fetch FAPs from API
   */
  const refreshFaps = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoadingFaps(true);
    setFapsError(null);

    try {
      const pageSize = 100;
      const firstPageResponse = await FaPsService.fapsControllerFindAll(1, pageSize);
      const firstPageFaps = getDataArray<FapResponseDto>(firstPageResponse);
      const totalPages = firstPageResponse.totalPages || 1;

      let allFaps = firstPageFaps;

      if (totalPages > 1) {
        const remainingPageResponses = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, index) =>
            FaPsService.fapsControllerFindAll(index + 2, pageSize),
          ),
        );

        const remainingFaps = remainingPageResponses.flatMap((response) =>
          getDataArray<FapResponseDto>(response),
        );
        allFaps = [...firstPageFaps, ...remainingFaps];
      }

      const transformedFaps = allFaps.map(transformFapToFrontend);
      setFaps(transformedFaps);
    } catch (error) {
      console.error('Error fetching FAPs:', error);
      setFapsError(error instanceof Error ? error.message : 'Failed to fetch FAPs');
    } finally {
      setIsLoadingFaps(false);
    }
  }, [isAuthenticated]);

  /**
   * Fetch Clients from API
   */
  const refreshClients = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoadingClients(true);
    setClientsError(null);

    try {
      // Fetch all clients (using a high limit to get all)
      const response = await ClientsService.clientsControllerFindAll(1, 100);

      // Response is already unwrapped by request.ts
      // Paginated response structure: { data: [...], total, page, limit, totalPages }
      const clientsData = getDataArray<Parameters<typeof transformClientToFrontend>[0]>(response);
      const transformedClients = clientsData.map(transformClientToFrontend);
      setClients(transformedClients);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClientsError(error instanceof Error ? error.message : 'Failed to fetch clients');
    } finally {
      setIsLoadingClients(false);
    }
  }, [isAuthenticated]);

  /**
   * Fetch Advisers from API
   * @param fapId Optional FAP ID to filter advisers
   * @param search Optional search term
   */
  const refreshAdvisers = useCallback(async (fapId?: string, search?: string) => {
    if (!isAuthenticated) return;

    setIsLoadingAdvisers(true);
    setAdvisersError(null);

    try {
      // Fetch advisers with optional filters
      const response = await UsersService.usersControllerFindAll(
        1, // page
        100, // limit (high to get all)
        search, // search
        undefined, // status
        fapId, // fapId filter
        'ADVISER', // role filter - only get advisers
        undefined, // adviserRole
        'createdAt', // sortBy
        'desc' // sortOrder
      );

      // Response is already unwrapped by request.ts
      // Paginated response structure: { data: [...], total, page, limit, totalPages }
      const advisersData = getDataArray<AdviserResponseDto>(response);
      const transformedAdvisers = advisersData.map(transformAdviserToFrontend);
      setAdvisers(transformedAdvisers);
    } catch (error) {
      console.error('Error fetching advisers:', error);
      setAdvisersError(error instanceof Error ? error.message : 'Failed to fetch advisers');
      // Keep mock data as fallback
    } finally {
      setIsLoadingAdvisers(false);
    }
  }, [isAuthenticated]);

  const refreshInvoices = useCallback(async () => {
    if (!isAuthenticated || !currentUser) return;

    if (!canViewInvoices(currentUser)) {
      setInvoices([]);
      return;
    }

    try {
      const response = await InvoicesService.invoicesControllerFindAll(1, 100);
      const invoicesData = getDataArray<Parameters<typeof transformInvoiceToFrontend>[0]>(response);
      const transformedInvoices = invoicesData.map(transformInvoiceToFrontend);
      setInvoices(transformedInvoices);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    }
  }, [currentUser, isAuthenticated]);

  const refreshRenewals = useCallback(async () => {
    if (!isAuthenticated || !currentUser) return;

    try {
      const response = await RenewalsService.renewalsControllerFindAll(1, 100);
      const renewalsData = getDataArray<Parameters<typeof transformRenewalToFrontend>[0]>(response);
      const transformedRenewals = renewalsData.map(transformRenewalToFrontend);
      setRenewals(transformedRenewals);
    } catch (error) {
      console.error('Error fetching renewals:', error);
      setRenewals([]);
    }
  }, [currentUser, isAuthenticated]);

  // Note: FAPs, Clients, and Invoices are fetched on-demand by the views that need them.

  // Map Auth0 user to internal User type
  useEffect(() => {
    if (auth0User && isAuthenticated && !auth0Loading) {
      const userProfile = auth0User as Auth0Profile;

      // Extract role from Auth0 user (could be in custom claims or user metadata)
      const roleClaim = userProfile['https://quotehub.com/roles'];
      const role = Array.isArray(roleClaim) && typeof roleClaim[0] === 'string'
        ? roleClaim[0]
        : '';

      // Map Auth0 user to internal User type
      // Auth0 provides given_name and family_name, or we can parse from name
      let firstName = userProfile.given_name || '';
      let lastName = userProfile.family_name || '';

      // If no given_name/family_name, try to parse from full name
      if (!firstName && !lastName && auth0User.name) {
        const nameParts = auth0User.name.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }

      const mappedUser: User = {
        id: auth0User.sub || auth0User.email || '',
        firstName: firstName || auth0User.email?.split('@')[0] || 'User',
        lastName: lastName || '',
        email: auth0User.email || '',
        role: role as User['role'],
      };

      // Preserve findMe-enriched fields when Auth0 remapping re-runs (e.g. on navigation).
      setCurrentUser((prev) =>
        prev && prev.id === mappedUser.id
          ? { ...mappedUser, adviserId: prev.adviserId, adviserRole: prev.adviserRole, fapId: prev.fapId }
          : mappedUser,
      );
      setDatadogRumUser({
        id: mappedUser.id,
        email: mappedUser.email,
        name: [mappedUser.firstName, mappedUser.lastName].filter(Boolean).join(' ') || undefined,
      });

      // Navigate to appropriate dashboard based on user role
      const dashboardPage =
        role === 'FINANCE_ADMIN'
          ? 'finance-admin-dashboard'
          : role === 'BUSINESS_ADMIN' || role === 'SUPER_ADMIN'
            ? 'business-admin-dashboard'
            : 'dashboard';

      if (currentPage === 'login') {
        setCurrentPage(dashboardPage);
      }
    } else if (!isAuthenticated && !auth0Loading && currentUser) {
      // User logged out
      clearDatadogRumUser();
      setCurrentUser(null);
      setCurrentPage('login');
    }
  }, [auth0User, isAuthenticated, auth0Loading, currentPage]);

  // Fetch adviser profile for operational users so permission checks and quote defaults work app-wide.
  // We use a ref to guard against double-fetching (e.g. React StrictMode) and to
  // reset when the user logs out and a new session begins.
  const adviserRoleFetchedRef = useRef(false);
  const userNeedsAdviserProfile =
    currentUser?.role === 'ADVISER' ||
    currentUser?.role === 'BUSINESS_ADMIN' ||
    currentUser?.role === 'SUPER_ADMIN';
  useEffect(() => {
    if (!isAuthenticated || !userNeedsAdviserProfile) {
      adviserRoleFetchedRef.current = false;
      return;
    }
    if (adviserRoleFetchedRef.current && currentUser?.adviserId) return;
    adviserRoleFetchedRef.current = true;

    const currentUserAuthId = currentUser.id;

    // Fetch the authenticated user's own profile directly.
    UsersService.usersControllerFindMe()
      .then((response) => {
        const adviserRole = response.adviserRole ?? null;
        const adviserId = response.adviserId ?? undefined;
        const fapId = response.fapId ?? undefined;
        setCurrentUser((prev) =>
          prev && prev.id === currentUserAuthId
            ? { ...prev, adviserRole, adviserId, fapId }
            : prev
        );
      })
      .catch((err) => {
        adviserRoleFetchedRef.current = false;
        console.warn('[AppContext] Failed to fetch adviserRole for current user:', err);
        setCurrentUser((prev) =>
          prev && prev.id === currentUserAuthId && prev.adviserRole === undefined
            ? { ...prev, adviserRole: null }
            : prev
        );
      });
  }, [isAuthenticated, currentUser?.id, currentUser?.role, currentUser?.adviserId, currentUser?.email, userNeedsAdviserProfile]);

  /**
   * Login function - DEPRECATED
   * This function is kept for backward compatibility but no longer supports mock/demo login.
   * All authentication is now handled by Auth0. The app automatically redirects to Auth0
   * login when not authenticated.
   * 
   * @deprecated Use Auth0 authentication instead. The app will automatically redirect to Auth0.
   */
  const login = (_email: string, _password: string): boolean => {
    console.warn(
      'login() is deprecated. Authentication is now handled by Auth0. ' +
      'The app will automatically redirect to Auth0 login when not authenticated.'
    );
    // Return false - no longer supports mock/demo login
    return false;
  };

  /**
   * Logout function - uses Auth0 logout
   */
  const logout = () => {
    // Clear local state
    setCurrentUser(null);
    setCurrentPage('login');
    setSelectedClient(null);
    setSelectedQuote(null);
    setSelectedRisk(null);
    setSelectedAsset(null);

    // Logout from Auth0
    if (isAuthenticated) {
      auth0Logout({
        logoutParams: {
          returnTo: getAuth0LogoutReturnTo(),
        },
      });
    }
  };

  const addClient = (client: Client) => {
    setClients([...clients, client]);
  };

  const updateClient = (client: Client) => {
    setClients(clients.map(c => c.id === client.id ? client : c));
  };

  const addQuote = (quote: Quote) => {
    setQuotes([...quotes, quote]);
  };

  const updateQuote = (quoteId: string, updates: Partial<Quote>) => {
    const updatedQuotes = quotes.map(q => q.id === quoteId ? { ...q, ...updates } : q);
    setQuotes(updatedQuotes);
    // Also update selectedQuote if it's the one being updated
    if (selectedQuote?.id === quoteId) {
      setSelectedQuote({ ...selectedQuote, ...updates });
    }
  };

  const addRisk = (risk: Risk) => {
    setRisks([...risks, risk]);
  };

  const updateRisk = (risk: Risk) => {
    setRisks(risks.map(r => r.id === risk.id ? risk : r));
  };

  const addAsset = (asset: Asset) => {
    setAssets([...assets, asset]);
  };

  const updateAsset = (asset: Asset) => {
    setAssets(assets.map(a => a.id === asset.id ? asset : a));
  };

  const updatePolicy = (policyId: string, updates: Partial<Policy>) => {
    const updatedPolicies = policies.map(p => p.id === policyId ? { ...p, ...updates } : p);
    setPolicies(updatedPolicies);
    // Also update selectedPolicy if it's the one being updated
    if (selectedPolicy?.id === policyId) {
      setSelectedPolicy({ ...selectedPolicy, ...updates });
    }
  };

  const updateTransaction = (transactionId: string, updates: Partial<Transaction>) => {
    const updatedTransactions = transactions.map(t => t.id === transactionId ? { ...t, ...updates } : t);
    setTransactions(updatedTransactions);
    // Also update selectedInvoice if it's the one being updated
    // Exclude Transaction-specific properties that are incompatible with Invoice
    if (selectedInvoice?.id === transactionId) {
      const {
        status,
        transactionType,
        transactionDate,
        transactionId: _,
        isAllocated,
        insurer, // Transaction.insurer is string, Invoice.insurer is Insurer type
        branch,
        authRepAndBroker,
        salesTeam,
        serviceTeam,
        particulars,
        dateReceivedFromInsurer,
        clientBalance,
        balance,
        ...safeUpdates
      } = updates;
      setSelectedInvoice({ ...selectedInvoice, ...safeUpdates });
    }
  };

  const addTransactions = (newTransactions: Transaction[]) => {
    setTransactions(prev => [...prev, ...newTransactions]);
  };

  const navigateWithHistory = (page: Page, _label: string, context?: { clientId?: string; quoteId?: string; assetId?: string }) => {
    // Don't add to history if it's the same page
    if (currentPage === page) return;

    // Add current page to history before navigating
    const currentLabel = getPageLabel(currentPage, selectedClient, selectedAsset);
    if (currentLabel && !['login', 'dashboard'].includes(currentPage)) {
      setNavigationHistory(prev => [...prev, {
        page: currentPage,
        label: currentLabel,
        clientId: selectedClient?.id,
        quoteId: selectedQuote?.id,
        assetId: selectedAsset?.id,
      }]);
    }

    // Set context if provided
    if (context?.clientId) {
      const client = clients.find(c => c.id === context.clientId);
      if (client) setSelectedClient(client);
    }
    if (context?.quoteId) {
      const quote = quotes.find(q => q.id === context.quoteId);
      if (quote) setSelectedQuote(quote);
    }
    if (context?.assetId) {
      const asset = assets.find(a => a.id === context.assetId);
      if (asset) setSelectedAsset(asset);
    }

    setCurrentPage(page);
  };

  const navigateBack = () => {
    if (navigationHistory.length === 0) {
      // Default fallback
      setCurrentPage('dashboard');
      return;
    }

    const previousPage = navigationHistory[navigationHistory.length - 1];

    // Restore context
    if (previousPage.clientId) {
      const client = clients.find(c => c.id === previousPage.clientId);
      if (client) setSelectedClient(client);
    }
    if (previousPage.quoteId) {
      const quote = quotes.find(q => q.id === previousPage.quoteId);
      if (quote) setSelectedQuote(quote);
    }
    if (previousPage.assetId) {
      const asset = assets.find(a => a.id === previousPage.assetId);
      if (asset) setSelectedAsset(asset);
    }

    // Navigate back
    setCurrentPage(previousPage.page);

    // Remove last item from history
    setNavigationHistory(prev => prev.slice(0, -1));
  };

  const getBreadcrumbs = (): NavigationHistoryItem[] => {
    const breadcrumbs = [...navigationHistory];

    // Add current page as the last breadcrumb
    const currentLabel = getPageLabel(currentPage, selectedClient, selectedAsset);
    if (currentLabel) {
      breadcrumbs.push({
        page: currentPage,
        label: currentLabel,
        clientId: selectedClient?.id,
        quoteId: selectedQuote?.id,
        assetId: selectedAsset?.id,
      });
    }

    return breadcrumbs;
  };

  const getPageLabel = (page: Page, client: Client | null, asset: Asset | null): string => {
    switch (page) {
      case 'dashboard': return 'Dashboard';
      case 'business-admin-dashboard': return 'Business Admin Dashboard';
      case 'clients': return 'Clients';
      case 'quotes': return 'Quotes';
      case 'renewals': return 'Renewals';
      case 'policies': return 'Policies';
      case 'invoices': return 'Invoices';
      case 'reports': return 'Reports';
      case 'client-profile': return client ? getClientName(client) : 'Client Profile';
      case 'quote-create': return 'Create Quote';
      case 'combined-quote-builder': return 'Create Combined Quote';
      case 'create-person': return 'Create Person';
      case 'create-organisation': return 'Create Organisation';
      case 'house-quote': return 'House Quote';
      case 'contents-quote': return 'Contents Quote';
      case 'vehicle-quote': return 'Vehicle Quote';
      case 'asset-detail': return asset ? asset.description : 'Asset Detail';
      case 'users': return 'Users';
      case 'fap-detail': return 'FAP Detail';
      case 'adviser-detail': return 'Adviser Detail';
      case 'user-detail': return 'User Detail';
      case 'debtors-report': return 'Debtors Report';
      case 'policy-detail': return 'Policy Detail';
      case 'eoffer-demo': return 'Eoffer Demo';
      case 'invoice-detail': return 'Invoice Detail';
      case 'renewal-detail': return 'Renewal Detail';
      case 'mta-workflow': return 'MTA Workflow';
      case 'cash-credits-report': return 'Cash Credits Report';
      case 'adviser-performance-report': return 'Adviser Performance Report';
      case 'bordeaux-report': return 'Bordereau Report';
      case 'notifications': return 'Notifications';
      default: return '';
    }
  };

  return (
    <AppContext.Provider
      value={{
        currentUser,
        login,
        logout,
        currentPage,
        setCurrentPage,
        navigateWithHistory,
        navigateBack,
        navigationHistory,
        getBreadcrumbs,
        clients,
        quotes,
        policies,
        risks,
        assets,
        invoices,
        payments,
        schedules,
        faps,
        advisers,
        renewals,
        transactions,
        selectedClient,
        setSelectedClient,
        selectedQuote,
        setSelectedQuote,
        selectedRisk,
        setSelectedRisk,
        selectedAsset,
        setSelectedAsset,
        selectedPolicy,
        setSelectedPolicy,
        selectedFAP,
        setSelectedFAP,
        selectedAdviser,
        setSelectedAdviser,
        selectedSystemUser,
        setSelectedSystemUser,
        selectedInvoice,
        setSelectedInvoice,
        selectedRenewal,
        setSelectedRenewal,
        addClient,
        updateClient,
        addQuote,
        updateQuote,
        addRisk,
        updateRisk,
        addAsset,
        updateAsset,
        updatePolicy,
        updateTransaction,
        addTransactions,
        searchQuery,
        setSearchQuery,
        isLoadingFaps,
        isLoadingClients,
        isLoadingAdvisers,
        fapsError,
        clientsError,
        advisersError,
        refreshFaps,
        refreshClients,
        refreshAdvisers,
        refreshInvoices,
        refreshRenewals,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};